// Typed Xano client for the GoEcom admin app.
//
// Single API group in workspace 8: 201 "Dashboard" (canonical 7cCIcwTp).
// Auth: JWT in localStorage, attached as `Authorization: Bearer ...`.
//
// The dashboard *engines* (/dashboard/*) historically resolve a company from a
// `?token=` magic link. The dual-resolver bridge (backend) lets the same engines
// resolve the company from the bearer token + an optional `company_id` query, so
// the authed SPA calls them directly. We pass `company_id` on every engine call.

const XANO_URL = (import.meta.env.VITE_XANO_URL as string | undefined) || "/xano";

const TOKEN_KEY = "goecom_auth_token";

export const tokenStore = {
  get(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  set(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
  },
};

export class XanoError extends Error {
  status: number;
  payload: unknown;
  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;
type FetchOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
};

function buildUrl(path: string, query?: Query): string {
  const absoluteBase = /^https?:\/\//i.test(XANO_URL)
    ? XANO_URL
    : window.location.origin + (XANO_URL.startsWith("/") ? XANO_URL : "/" + XANO_URL);
  const url = new URL(path.replace(/^\//, ""), absoluteBase.replace(/\/?$/, "/"));
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function request<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (e) {
    throw new XanoError(0, "Network error reaching GoEcom backend.", e);
  }

  const text = await res.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const message =
      (payload && typeof payload === "object" && "message" in payload
        ? String((payload as { message: unknown }).message)
        : null) || `Request failed (${res.status})`;
    if (res.status === 401) tokenStore.clear();
    throw new XanoError(res.status, message, payload);
  }
  return payload as T;
}

// ---- Types ----

export type User = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  job_title?: string;
  timezone?: string;
};

export type Company = {
  id: number;
  user_id: number;
  name: string;
  registration_number?: string;
  country?: string;
  company_type?: string;
  vat_registered?: boolean;
  vat_number?: string;
  currency?: string;
  status?: string;
  next_accounts_due?: string | null;
  next_confirmation_statement_due?: string | null;
  registered_address?: unknown;
  directors?: unknown;
};

export type CompanySettings = {
  checkout_mode: string;
  product_sync: string;
  order_import: string;
  transaction_posting: string;
};

export type AuthResponse = { authToken: string; user?: User };

export type TxJournalLine = { account: string; debit: number; credit: number };
export type TxRow = {
  id: number;
  order_id: number;
  status: "preview" | "approved" | "posted" | string;
  treatment_key: string;
  treatment_label: string;
  tax_code: string;
  journal: TxJournalLine[];
  expected_vat: number;
  miscoded: boolean;
  mode: string;
  posted_at?: number | null;
};
export type TxOrder = {
  id: number;
  order_number: string;
  shipping_country: string;
  shipping_country_code: string;
  order_created_at: number;
  total_price: number;
  subtotal_price: number;
  total_tax: number;
};
export type HsSuggestionItem = {
  code?: string;
  short_code?: string;
  description?: string;
  confidence?: "high" | "medium" | "low" | string;
  reasoning?: string;
};
export type HsSuggestion = {
  id: number;
  product_id: number;
  product_title: string;
  top_code: string | null;
  top_confidence: string | null;
  suggestions: HsSuggestionItem[];
  verified: boolean;
};
export type HsSuggestResult = {
  product_id: number;
  product_title: string;
  suggestions: HsSuggestionItem[];
  top_code: string | null;
  top_confidence: string | null;
  tariff_searched: number;
};

export type VatPackResponse = {
  company_id: number;
  company_name: string;
  generated_at: string;
  pack: {
    boxes: { box1_vat_due: number; box6_total_sales: number };
    zero_rated_exports: { net: number };
    ioss_by_country: { cc: string; country: string; orders: number; net: number; vat_due: number }[];
    ioss_vat_total: number;
    treatments: { key: string; label: string; orders: number; net: number; vat: number }[];
    corrections: { order: string; country: string; treatment: string; issue: string }[];
    order_count: number;
  };
};

export type ThresholdRadarItem = {
  key: string;
  label: string;
  threshold: number;
  threshold_label: string;
  current_annualised: number;
  pct_of_threshold: number;
  status: "registered" | "over" | "approaching" | "under" | string;
  months_to_threshold: number | null;
  registered: boolean;
  note: string;
};
export type ThresholdRadarResponse = {
  company_id: number;
  window_days: number;
  annual_total: number;
  annual_eu: number;
  radar: ThresholdRadarItem[];
};

export type ComplianceDeadline = {
  type: string;
  label: string;
  due_date: string;
  days_until: number;
  urgency: "urgent" | "soon" | "upcoming" | string;
  note: string;
};

export type BillingPlan = {
  id: number;
  plan_key: string;
  name: string;
  price: number;
  currency: string;
  blurb: string;
  features: string[];
};
export type AppSubscription = {
  id: number;
  plan_key: string;
  plan_name: string;
  price: number;
  status: string;
  test: boolean;
  current_period_end?: string | null;
};
export type BillingPlansResponse = { plans: BillingPlan[]; current: AppSubscription | null };

export type ProviderTaxCode = {
  treatment_key: string;
  label: string;
  sales_account_code: string;
  sales_tax_type: string;
  vat_account_code: string;
  vat_tax_type: string;
  debtor_account_code: string;
};
export type ProviderMapping = {
  company_id: number;
  provider: string;
  connected: boolean;
  provider_company: string;
  mappings: ProviderTaxCode[];
};

export type TxListResponse = {
  company_id: number;
  needs_stage: boolean;
  settings: CompanySettings | null;
  summary: { total: number; expected_vat_total: number };
  orders: TxOrder[];
  rows: TxRow[];
};

export type CompanyToken = {
  token: string;
  company_id: number;
  company_name: string;
  expires_at: string | null;
};

// ---- API surface ----
//
// The dashboard engines are token-gated. The SPA is bearer-authed, so it calls
// the `/app/company-token` bridge (which verifies ownership of the company) to
// obtain a stable, company-scoped `token`, then passes that token to every
// engine call below. This reuses the existing engines without editing them.

export const xano = {
  auth: {
    // /login (2377) is broken server-side (missing password_verify); /auth/login is the working replacement.
    login: (email: string, password: string) =>
      request<AuthResponse>("/auth/login", { method: "POST", body: { email, password } }),
    signup: (args: {
      first_name: string;
      last_name: string;
      email: string;
      password: string;
      source_site_slug?: string;
    }) => request<AuthResponse>("/signup", { method: "POST", body: args }),
    me: () => request<User>("/me"),
  },

  companies: {
    list: () => request<Company[]>("/companies"),
    get: (id: number) => request<Company>(`/companies/${id}`),
    create: (body: Partial<Company>) =>
      request<Company>("/companies", { method: "POST", body }),
    update: (id: number, body: Partial<Company>) =>
      request<Company>(`/companies/${id}`, { method: "PATCH", body }),
  },

  app: {
    // Bridge: bearer-authed, verifies ownership, returns a company-scoped engine token.
    companyToken: (companyId?: number) =>
      request<CompanyToken>("/app/company-token", {
        method: "POST",
        body: companyId ? { company_id: companyId } : {},
      }),
  },

  // Engine views. Each takes the bridge token (the engines resolve it to the company).
  dashboard: {
    overview: (token: string) =>
      request<Record<string, unknown>>("/dashboard", { query: { token } }),
    connections: (token: string) =>
      request<Record<string, unknown>>("/dashboard/connections", { query: { token } }),
    vatPosition: (token: string, monthsBack = 12) =>
      request<Record<string, unknown>>("/dashboard/vat-position", {
        query: { token, months_back: monthsBack },
      }),
    accountingMapping: (token: string) =>
      request<Record<string, unknown>>("/dashboard/accounting-mapping", { query: { token } }),
    aiSummary: (token: string) =>
      request<Record<string, unknown>>("/dashboard/ai-summary", { query: { token } }),
    settings: {
      get: (token: string) =>
        request<CompanySettings>("/dashboard/settings", { query: { token } }),
      set: (token: string, body: Partial<CompanySettings>) =>
        request<CompanySettings>("/dashboard/settings", {
          method: "POST",
          body: { token, ...body },
        }),
    },
  },

  compliance: {
    calendar: (token: string) =>
      request<{ company_id: number; deadlines: ComplianceDeadline[] }>(
        "/app/compliance/calendar",
        { query: { token } }
      ),
  },

  vatPack: (token: string) =>
    request<VatPackResponse>("/app/vat-pack", { query: { token } }),

  thresholdRadar: (token: string) =>
    request<ThresholdRadarResponse>("/app/threshold-radar", { query: { token } }),

  billing: {
    plans: (token: string) =>
      request<BillingPlansResponse>("/app/billing/plans", { query: { token } }),
    subscribe: (token: string, planKey: string, returnUrl?: string) =>
      request<{ ok: boolean; confirmation_url: string | null; http_status: number; shopify_response?: unknown }>(
        "/app/billing/subscribe",
        { method: "POST", body: { token, plan_key: planKey, return_url: returnUrl, test: true } }
      ),
    status: (token: string) =>
      request<{ current: AppSubscription | null; subscriptions: AppSubscription[] }>(
        "/app/billing/status",
        { query: { token } }
      ),
  },

  accounting: {
    mapping: (token: string) =>
      request<ProviderMapping>("/app/accounting/mapping", { query: { token } }),
    providerJournal: (token: string, orderId: number) =>
      request<Record<string, unknown>>("/app/transactions/provider-journal", {
        query: { token, order_id: orderId },
      }),
  },

  transactions: {
    list: (token: string) =>
      request<TxListResponse>("/app/transactions", { query: { token } }),
    refresh: (token: string) =>
      request<{ orders_total: number; newly_staged: number; already_staged: number }>(
        "/app/transactions/refresh",
        { method: "POST", body: { token } }
      ),
    setStatus: (token: string, orderIds: number[], action: "approve" | "post" | "reset") =>
      request<{ action: string; status: string; mode: string; changed: number; posting_setting: string }>(
        "/app/transactions/set-status",
        { method: "POST", body: { token, order_ids: orderIds, action } }
      ),
  },

  products: {
    suggestions: (token: string) =>
      request<{ company_id: number; suggestions: HsSuggestion[] }>(
        "/app/products/suggestions",
        { query: { token } }
      ),
    suggestHs: (token: string, productId: number) =>
      request<HsSuggestResult>("/app/products/suggest-hs", {
        method: "POST",
        body: { token, product_id: productId },
      }),
  },

  catalogue: (token: string) =>
    request<Record<string, unknown>>("/shopify/catalogue", { query: { token } }),

  marketIntel: (hsCode: string) =>
    request<Record<string, unknown>>("/uk-trade-info/market-intel", {
      query: { hs_code: hsCode },
    }),

  raw: request,
};
